import { useEffect, useState } from "react";
import NextLink from "next/link";
import { type SubmitHandler, useForm, useWatch } from "react-hook-form";
import {
  Box,
  Button,
  ErrorText,
  Heading,
  Loader,
  Text,
  Link,
  Radio,
} from "@cruk/cruk-react-components";
import { useRouter } from "next/compat/router";

import { useTracking } from "@fwa/src/hooks/useTracking";
import { useSwrGet } from "@fwa/src/hooks/useSwrGet";
import { createTeamMember } from "@fwa/src/services/teamMemberService";
import { useBrandContext } from "@fwa/src/contexts/BrandContext";
import { useFundraiserContext } from "@fwa/src/contexts/FundraiserContext";
import {
  fwsUrlFundraiserPagesAvaliableForExistingTeam,
  fwsUrlFundraiserMembershipsForPage,
} from "@fwa/src/services/fundraiserService";
import { logIn } from "@fwa/src/services/auth";
import { sendGTMTrackingEvent } from "@fwa/src/services/gtm";

import sharedStyles from "@fwa/src/components/styles.module.css";
import styles from "@fwa/src/components/JoinPage/styles.module.css";

import type {
  BrandNameType,
  FundraisingPageType,
  FwsResponseData,
  SuperTeamPageType,
  TeamMemberType,
  TeamPageType,
} from "@fwa/src/types";
import { useModalContext } from "@fwa/src/contexts/ModalContext";

export interface TeamJoinValues {
  fundraisingPageUniqueId: string;
}

const initialValues: TeamJoinValues = {
  fundraisingPageUniqueId: "",
};

export const JoinPage = ({
  pageData: team,
  themeName,
}: {
  pageData: TeamPageType | SuperTeamPageType;
  themeName?: string;
}) => {
  const { trackError, trackEventGtm } = useTracking();
  const isSuperTeam = team.entityType === "SuperTeam";
  const router = useRouter();
  const [fundraiserState] = useFundraiserContext();
  const [brandName, setBrandName] = useBrandContext();
  const isLoggedOut = fundraiserState.loggedInStatus === "loggedOut";
  const isLoggedIn = fundraiserState.loggedInStatus === "loggedIn";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState("");
  const [modalContext, setModalContext] = useModalContext();

  const membersInTeamUrl =
    fundraiserState.fundraiser && team
      ? fwsUrlFundraiserMembershipsForPage({
          teamId: team.uniqueId,
          fundraiserId: fundraiserState.fundraiser.uniqueId,
        })
      : null;
  const { data: membersData, error: existingPagesError } =
    useSwrGet<FwsResponseData<TeamMemberType>>(membersInTeamUrl);
  const membersInTeam = membersData?.results || [];

  if (existingPagesError) {
    trackError(existingPagesError, { component: "JoinPage" });
  }

  const availablePagesUrl =
    fundraiserState.fundraiser && team
      ? fwsUrlFundraiserPagesAvaliableForExistingTeam({
          teamId: team.uniqueId,
          fundraiserId: fundraiserState.fundraiser.uniqueId,
          isSuperTeam,
        })
      : null;
  const { data: pagesData, error: availablePagesError } =
    useSwrGet<FwsResponseData<FundraisingPageType>>(availablePagesUrl);
  const availablePages = pagesData?.results || [];

  if (availablePagesError) {
    trackError(availablePagesError, { component: "JoinPage" });
  }

  const { handleSubmit, getValues, register, control } =
    useForm<TeamJoinValues>({
      mode: "onBlur",
      reValidateMode: "onBlur",
      defaultValues: initialValues,
      criteriaMode: "firstError",
      shouldFocusError: true,
      resetOptions: {
        keepDirtyValues: true, // user-interacted input will be retained
        keepErrors: false, // input errors will be retained with value update
      },
    });

  const watchFundraisingPageUniqueId = useWatch({
    control,
    name: "fundraisingPageUniqueId",
  });

  // Keeps react hook form happy
  const onSubmit = handleSubmit((data) => {
    submit(data);
  });

  // Keeps react hook form happy
  const submit: SubmitHandler<TeamJoinValues> = async (
    values,
    e,
  ): Promise<void> => {
    if (e) e.preventDefault();
    await doSubmit();
  };

  const doSubmit = async () => {
    setIsSubmitting(true);
    const values = getValues();
    // join team
    await createTeamMember({
      data: { membershipStatus: "member" },
      childPageUniqueId: values.fundraisingPageUniqueId,
      parentPageUniqueId: team.uniqueId,
    })
      .then(() => {
        trackEventGtm({ event: "team_joined" });
        const slug = team.url.slice(team.url.lastIndexOf("/") + 1);
        return router?.push({
          pathname: `/${isSuperTeam ? "unite" : "team"}/${slug}`,
        });
      })
      .catch((err) => {
        trackError(err as Error, { component: "JoinPage" });
        setStatus("Sorry something went wrong, please try later.");
        setIsSubmitting(false);
      });
  };

  // In order for this page to work we need to make sure that the user is logged in
  useEffect(() => {
    // if user is logged out they don't see the choose page form yet they have to choose
    // to go though the sign up journey or log in and go through the join page journey
    if (isLoggedIn) {
      sendGTMTrackingEvent({
        event: "pageView",
        pagePath: "/team-join/team-search/choose-page",
        pageTitle: "join a team | choose page",
      });
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (!!themeName && themeName !== brandName) {
      setBrandName(themeName as BrandNameType);
    }
  }, [themeName, brandName, setBrandName]);

  useEffect(() => {
    // if we are redirect here from a fundraising page and on success we will redirect to a team page
    // we don't want any fundraising page modals accidentally appearing on the team page
    if (modalContext) {
      setModalContext(null);
    }
  }, [modalContext, setModalContext]);

  return (
    <Box
      data-component="create-account-page-prompt"
      backgroundColor="background-light"
      paddingHorizontal="s"
      paddingVertical="l"
      marginTop="s"
    >
      {isLoggedOut ? (
        <div className={sharedStyles["content-wrapper-responsive"]}>
          <Heading h1>Join {team.title}</Heading>
          <Text>To join this page you need an account and a Giving Page</Text>
          <div className={sharedStyles["row-center"]}>
            <Box marginVertical="m">
              <NextLink
                prefetch={false}
                href={`/createpage?${
                  isSuperTeam ? "super-team-uuid" : "team-uuid"
                }=${team.uniqueId}`}
                data-cta-type="create-page"
              >
                <Button as="span">Create a new Giving Page</Button>
              </NextLink>
            </Box>
          </div>
          <div className={"divider"}>OR</div>
          <div className={sharedStyles["row-center"]}>
            <Box marginVertical="m">
              <Button
                appearance="secondary"
                onClick={() => {
                  logIn();
                }}
                data-cta-type="log-in"
              >
                Log in to my account
              </Button>
            </Box>
          </div>
        </div>
      ) : (
        <div
          className={sharedStyles["form-wrapper"]}
          data-component="join-team-form"
        >
          <Heading h1>Join {team.title}</Heading>
          {!membersInTeam || !availablePages ? (
            <Loader />
          ) : (
            <>
              {/* The fundraiser already has pages in the team. Suggest they use these before joining or creating more */}
              {!!membersInTeam.length && (
                <>
                  <Heading textSize="xl">
                    It looks like you already have a Giving Page in this team
                  </Heading>
                  <ul className={styles["page-list"]}>
                    {membersInTeam.map(
                      ({ childPage }: { childPage: FundraisingPageType }) => (
                        <li key={childPage.uniqueId}>
                          <NextLink
                            href={`/page${childPage.url.slice(
                              childPage.url.lastIndexOf("/"),
                            )}`}
                            data-cta-type="link-page"
                          >
                            <Link appearance="primary" as="span">
                              View {childPage.title}
                            </Link>
                          </NextLink>
                        </li>
                      ),
                    )}
                  </ul>
                </>
              )}
              {/* The fundraiser has pages that can be joined to the team, show form to select page */}
              {availablePages.length ? (
                <form
                  noValidate
                  onSubmit={(e) => {
                    onSubmit(e).catch((err) => {
                      trackError(err as Error, {
                        component: "LogInWrapperFrom",
                      });
                    });
                  }}
                >
                  <fieldset className={styles["fieldset"]}>
                    {membersInTeam.length ? (
                      <legend className={styles["legend"]}>
                        To add another Giving Page to this team please select
                        one below
                      </legend>
                    ) : (
                      <legend className={styles["legend"]}>
                        Please select the Giving Page to link to this team
                        <Text marginTop="xxs">
                          You need to have a Giving Page to join a team. Pages
                          can only be in one team at a time and you cannot add a
                          page that has closed.
                        </Text>
                      </legend>
                    )}
                    {availablePages.map(
                      (fundraisingPage: FundraisingPageType) => (
                        <Radio
                          {...register("fundraisingPageUniqueId")}
                          key={fundraisingPage.uniqueId}
                          value={fundraisingPage.uniqueId}
                        >
                          {fundraisingPage.title}
                        </Radio>
                      ),
                    )}
                  </fieldset>
                  {status.length ? (
                    <Box>
                      <ErrorText>{status}</ErrorText>
                    </Box>
                  ) : null}
                  <Box marginBottom="xl">
                    <Button
                      type="submit"
                      appearance="primary"
                      disabled={
                        isSubmitting || !watchFundraisingPageUniqueId.length
                      }
                      data-cta-type="choose-page"
                    >
                      Choose this Giving Page
                    </Button>
                  </Box>
                  <div className={sharedStyles["divider"]}>OR</div>
                </form>
              ) : (
                <Text marginTop="xxs">
                  You need to have a Giving Page to join a team. Pages can only
                  be in one team at a time and you cannot add a page that has
                  closed.
                </Text>
              )}
              {/* In all cases let fundraiser create new page to join to team */}
              <Box marginTop="xl">
                <NextLink
                  href={`/createpage?${
                    isSuperTeam ? "super-team-uuid" : "team-uuid"
                  }=${team.uniqueId}`}
                  data-cta-type="create-page"
                >
                  <Button
                    appearance={availablePages.length ? "secondary" : "primary"}
                    as="span"
                  >
                    Create a new Giving Page
                  </Button>
                </NextLink>
              </Box>
            </>
          )}
        </div>
      )}
    </Box>
  );
};

export default JoinPage;
